/**
 * ArtifactIndex — builds the artifact manifest by scanning run folders.
 *
 * Scans the artifacts directory for timestamped run folders,
 * computes file sizes, and produces an ArtifactRunManifest.
 */

import { readdirSync, statSync } from 'node:fs';
import { join, extname, basename } from 'node:path';
import type {
  ArtifactRunManifest,
  ArtifactRunEntry,
  ArtifactTestEntry,
  ArtifactFile,
} from '@testrelic/core';

const TIMESTAMP_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}-\d{2}-\d{2}(-\d+)?$/;
const MANIFEST_SCHEMA_VERSION = '1.0';

/** Subdirectory holding visual baseline comparisons within a test folder. */
const VISUAL_DIR = 'visual';

function classifyFileType(name: string): ArtifactFile['type'] {
  const ext = extname(name).toLowerCase();
  if (['.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp'].includes(ext)) return 'screenshot';
  if (['.webm', '.mp4', '.avi', '.mov'].includes(ext)) return 'video';
  // Match on the NAME, not just the extension: `.zip` alone says nothing about
  // what is inside, and the collector writes this file as `trace.zip`.
  if (ext === '.zip' && basename(name, ext) === 'trace') return 'trace';
  return 'other';
}

/**
 * Classify a file inside a `visual/<snapshot>/` folder.
 *
 * These are all images, so extension alone would call every one of them a
 * screenshot and the manifest could not tell a baseline from the render that
 * disagreed with it. The writer controls these names, so the stem is reliable.
 */
function classifyVisualFileType(name: string): ArtifactFile['type'] {
  if (name.endsWith('.dom.json')) return 'dom';
  const stem = basename(name, extname(name));
  if (stem === 'baseline') return 'baseline';
  if (stem === 'diff') return 'diff';
  return classifyFileType(name);
}

/** Collect one snapshot folder's images, e.g. `visual/home--a1b2c3/`. */
function scanVisualFolder(
  snapshotDir: string,
  relativeBase: string,
  snapshotName: string,
): ArtifactFile[] {
  const files: ArtifactFile[] = [];
  try {
    for (const entry of readdirSync(snapshotDir, { withFileTypes: true })) {
      if (!entry.isFile()) continue;
      const stat = statSync(join(snapshotDir, entry.name));
      files.push({
        name: `${VISUAL_DIR}/${snapshotName}/${entry.name}`,
        type: classifyVisualFileType(entry.name),
        relativePath: `${relativeBase}/${VISUAL_DIR}/${snapshotName}/${entry.name}`,
        sizeBytes: stat.size,
      });
    }
  } catch {
    // Skip unreadable snapshot folders
  }
  return files;
}

function scanTestFolder(testDir: string, runFolder: string, testName: string): ArtifactTestEntry {
  const files: ArtifactFile[] = [];
  const relativeBase = `artifacts/${runFolder}/${testName}`;
  try {
    const entries = readdirSync(testDir, { withFileTypes: true });
    for (const entry of entries) {
      // Visual comparisons are the one nested case: a test can hold many named
      // snapshots, so they live a level down rather than flat beside the video.
      if (entry.isDirectory() && entry.name === VISUAL_DIR) {
        const visualDir = join(testDir, entry.name);
        for (const snapshot of readdirSync(visualDir, { withFileTypes: true })) {
          if (!snapshot.isDirectory()) continue;
          files.push(
            ...scanVisualFolder(join(visualDir, snapshot.name), relativeBase, snapshot.name),
          );
        }
        continue;
      }
      if (!entry.isFile()) continue;
      const filePath = join(testDir, entry.name);
      const stat = statSync(filePath);
      files.push({
        name: entry.name,
        type: classifyFileType(entry.name),
        relativePath: `${relativeBase}/${entry.name}`,
        sizeBytes: stat.size,
      });
    }
  } catch {
    // Skip unreadable directories
  }
  return { testName, files };
}

function scanRunFolder(artifactsDir: string, folderName: string): ArtifactRunEntry {
  const runDir = join(artifactsDir, folderName);
  const tests: ArtifactTestEntry[] = [];
  let totalSizeBytes = 0;

  try {
    const entries = readdirSync(runDir, { withFileTypes: true });
    for (const entry of entries) {
      if (!entry.isDirectory()) continue;
      const testEntry = scanTestFolder(join(runDir, entry.name), folderName, entry.name);
      tests.push(testEntry);
      for (const file of testEntry.files) {
        totalSizeBytes += file.sizeBytes;
      }
    }
  } catch {
    // Skip unreadable directories
  }

  // Parse timestamp from folder name: 2026-03-02T14-30-00 → 2026-03-02T14:30:00
  const timestamp = folderName
    .replace(/^(\d{4}-\d{2}-\d{2})T(\d{2})-(\d{2})-(\d{2})/, '$1T$2:$3:$4')
    .replace(/-\d+$/, ''); // Remove collision suffix for ISO parsing

  tests.sort((a, b) => a.testName.localeCompare(b.testName));

  return {
    folderName,
    timestamp,
    totalSizeBytes,
    testCount: tests.length,
    tests,
    isCurrentRun: false,
  };
}

/**
 * Build an artifact manifest by scanning the artifacts directory.
 *
 * Discovers timestamped run folders, scans their contents,
 * and returns a sorted manifest (newest first).
 * Legacy flat folders (non-timestamped) are grouped under a "Legacy" entry.
 */
export function buildArtifactManifest(
  outputDir: string,
  currentRunTimestamp: string,
): ArtifactRunManifest {
  const artifactsDir = join(outputDir, 'artifacts');
  const runs: ArtifactRunEntry[] = [];
  const legacyTests: ArtifactTestEntry[] = [];
  let legacySize = 0;

  try {
    const entries = readdirSync(artifactsDir, { withFileTypes: true });
    for (const entry of entries) {
      if (!entry.isDirectory()) continue;

      if (TIMESTAMP_PATTERN.test(entry.name)) {
        const run = scanRunFolder(artifactsDir, entry.name);
        runs.push({
          ...run,
          isCurrentRun: entry.name === currentRunTimestamp,
        });
      } else {
        // Legacy flat folder — treat as a test entry
        const testEntry = scanTestFolder(join(artifactsDir, entry.name), entry.name, entry.name);
        legacyTests.push(testEntry);
        for (const file of testEntry.files) {
          legacySize += file.sizeBytes;
        }
      }
    }
  } catch {
    // Artifacts directory doesn't exist or is unreadable — return empty manifest
  }

  // Sort runs by timestamp descending (newest first)
  runs.sort((a, b) => b.timestamp.localeCompare(a.timestamp));

  // Add legacy group if any non-timestamped folders exist
  if (legacyTests.length > 0) {
    legacyTests.sort((a, b) => a.testName.localeCompare(b.testName));
    runs.push({
      folderName: '__legacy__',
      timestamp: '1970-01-01T00:00:00',
      totalSizeBytes: legacySize,
      testCount: legacyTests.length,
      tests: legacyTests,
      isCurrentRun: false,
    });
  }

  const totalSizeBytes = runs.reduce((sum, r) => sum + r.totalSizeBytes, 0);

  return {
    schemaVersion: MANIFEST_SCHEMA_VERSION,
    generatedAt: new Date().toISOString(),
    artifactBaseDir: 'artifacts',
    totalSizeBytes,
    runs,
    serverPort: null,
  };
}
